import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import CoverImage, { StyledCoverImage } from './CoverImage'
import Message, { StyledMessage } from '../../../components/form/Message'
import { getArtwork, getArtworkNumber, getCardImage, getColor, removeDuplicateCards } from '../../../helper/card'
import Tooltip from '../../../components/general/Tooltip'
import { desktop } from '../../../styles/media'
import Link from '../../../components/router/Link'
import { setReference } from '../../../state/actions/app'
import references from '../../../data/references'

const CoverImageSelect = ({ cardNameLanguage, deck, onChange, setReference, subscribed, t }) => {
	const deckCardsWithoutDuplicates = removeDuplicateCards(deck.cards)
	const deckCards = [
		...deck.archetypes.reduce((accumulator, archetype) => {
			if (archetype && !deckCardsWithoutDuplicates.find(item => item.card.slug === archetype.coverCardSlug)) {
				accumulator.push({
					card: {
						image_en: archetype.image_en,
						image_de: archetype.image_de,
						slug: archetype.coverCardSlug,
						name_en: archetype.coverCardName_en,
						name_de: archetype.coverCardName_de,
						color: archetype.color
					},
					...(archetype.coverArtworkId && ({
						artwork: {
							id: archetype.coverArtworkId,
							image: archetype.image,
							color: archetype.color
						}
					}))
				})
			}

			return accumulator
		}, []),
		...deckCardsWithoutDuplicates
	]

	return (
		<Wrapper>
			{!subscribed && deckCards.length !== 0 && (
				<Message primary light icon="information-circle">
					<strong>{t('general.coverCardNotSubscribed')}</strong><br />
					{t('general.coverCardNotSubscribed2')} <Link to="/pricing" onBeforeNavigate={() => setReference(references.DECK_COVER_CARD)}>{t('general.unlockNow')}</Link>
				</Message>
			)}

			<CoverImageList>
				{deckCards.map((deckCard) => {
					const selected = !!((deck.coverCard && deck.coverCard.slug === deckCard.card.slug && !deckCard.artworkId)
						|| (deck.coverArtwork && deckCard.artworkId && deck.coverArtwork.id === deckCard.artworkId))
					const name = deckCard.card[`name_${cardNameLanguage}`] || deckCard.card.name_en

					return (
						<CoverImageOption
							onClick={(event) => {
								event.preventDefault()

								const type = deckCard.artworkId ? 'coverArtwork' : 'coverCard'
								let value = null

								if (!selected) {
									if (type === 'coverArtwork') {
										value = getArtwork(deckCard)
									} else {
										value = deckCard.card
									}

									onChange(type, value)
								}
							}}
							selected={selected}
							key={`${deckCard.card.slug}-${deckCard.artworkId}`}
						>
							<Tooltip title={`${name}${deckCard.artworkId ? ` (${t('general.artwork')} #${getArtworkNumber(deckCard)})` : ''}`}>
								<CoverImage
									large
									deck={{
										...deck,
										user: {
											...deck.user,
											subscribed: 'pro'
										}
									}}
									card={{
										...deckCard.card,
										image: getCardImage(deckCard, cardNameLanguage),
										color: getColor(deckCard)
									}}
									selected={selected}
								/>
							</Tooltip>
						</CoverImageOption>
					)
				})}
			</CoverImageList>

			{deckCards.length === 0 && (
				<MessageContainer>
					<Message info icon="information-circle">{t('general.coverCardEmptyDeck')}</Message>
				</MessageContainer>
			)}
		</Wrapper>
	)
}

CoverImageSelect.propTypes = {
	cardNameLanguage: PropTypes.string,
	deck: PropTypes.object.isRequired,
	onChange: PropTypes.func.isRequired,
	subscribed: PropTypes.string,
	setReference: PropTypes.func.isRequired,
	t: PropTypes.func.isRequired
}

CoverImageSelect.defaultProps = {
	cardNameLanguage: null,
	subscribed: null
}

const Wrapper = styled.div`
	padding: 0.5rem 0;
`

const MessageContainer = styled.div`
	margin-top: -1rem;

	${StyledMessage} {
		margin: 0;
	}
`

export const CoverImageList = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(55px, 1fr));
	grid-gap: 1rem;
`

const CoverImageOption = styled.button`
	background: transparent;
	border: 0;
	transition: opacity 0.2s;
	padding: 0;

	${desktop`
		${StyledCoverImage} {
			opacity: ${props => props.selected ? '1' : '0.5'};

			&:hover {
				opacity: 1;
			}
		}
	`}
`

const enhance = connect(state => ({
	cardNameLanguage: state.persist.user.settings['app.cardNameLanguage']
}), dispatch => bindActionCreators({
	setReference
}, dispatch))

export default enhance(CoverImageSelect)
